import {
  ButtonHTMLAttributes,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { InputSize, Select, Show } from "shared/components";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { cx } from "shared/utils";
import { CountNotification } from "../CountNotification";
import { ActiveTab } from "../Table/constants";
import { computeOverflowLayout, OverflowLayout, STRIP_PADDING } from "./utils";

export interface ApplicationTabItem {
  value: ActiveTab;
  label: string;
  count?: number;
}

const sameValues = (a: ActiveTab[], b: ActiveTab[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const tabButtonClass = (active: boolean) =>
  cx(
    "rounded-md border px-4 py-2 text-xs whitespace-nowrap transition",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40",
    active
      ? "border-alpha-100 bg-white font-bold text-content-1000 shadow-sm"
      : "border-transparent font-medium text-content-300 hover:text-content-1000"
  );

const TabButton = ({
  tab,
  active,
  className,
  ...rest
}: {
  tab: ApplicationTabItem;
  active: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    className={cx(tabButtonClass(active), className)}
    {...rest}
  >
    <span className="flex items-center justify-center gap-2">
      {tab.label}
      <CountNotification totalCount={tab.count} />
    </span>
  </button>
);

const MoreButton = forwardRef<
  HTMLButtonElement,
  {
    open: boolean;
    hasCount: boolean;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(({ open, hasCount, className, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-haspopup="menu"
    aria-expanded={open}
    className={cx(
      tabButtonClass(false),
      "flex-none",
      open && "bg-alpha-40 text-content-1000",
      className
    )}
    {...rest}
  >
    <span className="flex items-center justify-center gap-1">
      More
      <Show if={hasCount}>
        <span className="size-1.5 rounded-full bg-primary-500" />
      </Show>
      <HugeiconsIcon
        icon={ArrowDown01Icon}
        size={14}
        strokeWidth={2}
        className={cx("transition-transform", open && "rotate-180")}
      />
    </span>
  </button>
));
MoreButton.displayName = "MoreButton";

export const ApplicationTabs = ({
  tabs,
  selected,
  onChange
}: {
  tabs: ApplicationTabItem[];
  selected: ActiveTab;
  onChange: (tab: ActiveTab) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const measureMoreRef = useRef<HTMLButtonElement>(null);
  const [layout, setLayout] = useState<OverflowLayout>({
    visible: tabs.map((tab) => tab.value),
    overflow: []
  });
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  // Latest props for the observer callback, so it can stay stable and
  // re-measure after the parent re-renders (e.g. counts arriving).
  const latest = useRef({ tabs, selected });
  latest.current = { tabs, selected };

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    const measureMore = measureMoreRef.current;
    if (!container || !measure || !measureMore) return;

    const widths = Array.from(measure.children)
      .filter((element) => element !== measureMore)
      .map((element) => element.getBoundingClientRect().width);

    const next = computeOverflowLayout({
      values: latest.current.tabs.map((tab) => tab.value),
      widths,
      activeValue: latest.current.selected,
      moreWidth: measureMore.getBoundingClientRect().width,
      availableWidth: container.clientWidth - STRIP_PADDING
    });

    setLayout((previous) =>
      sameValues(previous.visible, next.visible) &&
      sameValues(previous.overflow, next.overflow)
        ? previous
        : next
    );
  }, []);

  // Re-measure on every render (labels/counts may have changed) …
  useLayoutEffect(() => {
    recompute();
  });

  // … and whenever the strip or the measured labels change size.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const observer = new ResizeObserver(() => recompute());
    observer.observe(container);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [recompute]);

  const tabByValue = (value: ActiveTab) =>
    tabs.find((tab) => tab.value === value);
  const overflowTabs = layout.overflow
    .map(tabByValue)
    .filter((tab): tab is ApplicationTabItem => Boolean(tab));
  const overflowHasCount = overflowTabs.some((tab) => Boolean(tab.count));

  const handleChange = (value: ActiveTab) => {
    setIsMoreOpen(false);
    onChange(value);
  };

  return (
    <>
      <Select
        className="md:hidden w-full"
        id="application-tab-selector"
        value={selected}
        legacyErrorMargins={false}
        size={InputSize.SM}
        onChange={(e) => onChange(e.target.value as ActiveTab)}
        options={tabs.map((tab) => ({
          value: tab.value,
          label: (
            <p className="flex gap-2">
              {tab.label}
              <CountNotification totalCount={tab.count} />
            </p>
          )
        }))}
      />

      {/*
        The outer box spans the available width and is what gets measured;
        the pill inside hugs the tabs that fit, so they keep their natural
        label widths instead of stretching to fill the row.
      */}
      <div
        ref={containerRef}
        className="relative hidden w-full min-w-0 md:block"
      >
        <div
          role="tablist"
          className="flex w-fit max-w-full gap-1 rounded-lg bg-alpha-40 p-1"
        >
          {layout.visible
            .map(tabByValue)
            .map((tab) =>
              tab ? (
                <TabButton
                  key={tab.value}
                  tab={tab}
                  active={tab.value === selected}
                  onClick={() => handleChange(tab.value)}
                />
              ) : null
            )}

          <Show if={overflowTabs.length > 0}>
            <Popover
              open={isMoreOpen}
              onOpenChange={setIsMoreOpen}
              placement="bottom-end"
            >
              <PopoverTrigger asChild>
                <MoreButton
                  open={isMoreOpen}
                  hasCount={overflowHasCount}
                  // The shared Popover leaves click handling to the trigger
                  // when `open` is controlled.
                  onClick={() => setIsMoreOpen((value) => !value)}
                />
              </PopoverTrigger>
              <PopoverContent className="z-20 min-w-[220px] rounded-lg border border-alpha-100 bg-white p-1 shadow-lg focus:outline-none">
                <div role="menu" className="space-y-0.5">
                  {overflowTabs.map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      role="menuitem"
                      className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-xs font-medium text-content-1000 transition hover:bg-alpha-40 focus:outline-none focus-visible:bg-alpha-40"
                      onClick={() => handleChange(tab.value)}
                    >
                      {tab.label}
                      <CountNotification totalCount={tab.count} />
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </Show>
        </div>

        {/*
          Measurement copy of every tab in the exact weight it renders with
          (the active one bold, the rest medium), so the strip collapses only
          when the tabs truly stop fitting. It never paints, and the
          zero-height clipping box keeps it from adding
          horizontal scroll to the page when it is wider than the strip; the
          overflow layout is derived from its children's widths.
        */}
        <div
          aria-hidden
          className="absolute left-0 top-0 h-0 w-full overflow-hidden"
        >
          <div ref={measureRef} className="invisible flex w-max gap-1 p-1">
            {tabs.map((tab) => (
              <TabButton
                key={tab.value}
                tab={tab}
                active={tab.value === selected}
                tabIndex={-1}
              />
            ))}
            <MoreButton
              ref={measureMoreRef}
              open={false}
              hasCount
              tabIndex={-1}
            />
          </div>
        </div>
      </div>
    </>
  );
};
